export const RedirectContext = Object.freeze({
  INDEX: 'index',
  LOGIN: 'login',
  POST_TRIAL: 'postTrial',
  FINALIZE: 'finalize',
})

/**
 * Routes that must never be stored as (or used as) a return target after login.
 * They either only render a loader (and would leave the user stuck) or their token
 * is single use, so returning to them can never succeed.
 */
export const NON_RETURNABLE_PREFIXES = Object.freeze([
  '/login',
  '/account/login',
  '/account/register',
  '/account/finalize',
  '/account/approve', // covers /approve/:token, /approved, /approve_resend/:email, /approvechangeemail/:token
  '/account/accept_invitation',
  '/account/forgot_password',
  '/account/reset_password',
  '/account/delete',
  '/account/undo-tenant-deletion',
  '/admin/login',
])

export const isNonReturnableRoute = (pathname) => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return true

  return NON_RETURNABLE_PREFIXES.some(prefix => pathname.startsWith(prefix))
}

export const getPostLoginRedirectDecision = (input = {}) => {
  const {
    context,
    isMeinBuero = false,
    subscriptionLoading = false,
    hasTrialSub = false,
    hasPaidSub = false,
    hasOnlyDocumentTrial = false,
    isDemo = false,
    returnAfterLogin = null,
    defaultPath = '/dashboard',
    noRedirect = false,
  } = input

  if (context === RedirectContext.INDEX) {
    if (subscriptionLoading) {
      return { blockRedirect: true, reason: 'subscription-loading' }
    }
    if (hasPaidSub && !isDemo) {
      return { target: defaultPath, reason: 'paid-sub' }
    }
    if ((hasTrialSub && !hasPaidSub) || isDemo) {
      return { target: '/firstSteps', reason: 'trial-or-demo-firststeps' }
    }
    return { target: defaultPath, reason: 'fallback' }
  }

  if (context === RedirectContext.POST_TRIAL) {
    if (isMeinBuero && hasOnlyDocumentTrial) {
      return { target: '/documents', reason: 'post-trial-doc-only' }
    }
    return { target: '/firstSteps', reason: 'post-trial-firststeps' }
  }

  if (context === RedirectContext.LOGIN) {
    // guard against a poisoned return target that would send the user back to a loader-only route
    if (returnAfterLogin && !noRedirect && !isNonReturnableRoute(returnAfterLogin)) {
      return { target: returnAfterLogin, reason: 'return-after-login' }
    }
    if (noRedirect) {
      return { target: undefined, reason: 'no-redirect' }
    }
    return { target: defaultPath, reason: 'login-default' }
  }

  return { target: defaultPath, reason: 'default' }
}
